<script setup lang="ts">
import { defineProps, ref, computed } from "vue";
import { CalendarDayHour, ChineseCalendarAction } from "@/types/calculators";
import DayCardComponent from "@/views/Calculators/ChineseCalendar/components/DayCardComponent.vue";
import { useI18n } from "vue-i18n";

type TimeInterval = { start: number; end: number };

defineProps<{
  hoursTable: CalendarDayHour[];
}>();

const { t } = useI18n();

const baseHours: TimeInterval[] = [
  { start: 0, end: 60 }, // 00:00 – 01:00
  { start: 60, end: 180 }, // 01:00 – 03:00
  { start: 180, end: 300 }, // 03:00 – 05:00
  { start: 300, end: 420 }, // 05:00 – 07:00
  { start: 420, end: 540 }, // 07:00 – 09:00
  { start: 540, end: 660 }, // 09:00 – 11:00
  { start: 660, end: 780 }, // 11:00 – 13:00
  { start: 780, end: 900 }, // 13:00 – 15:00
  { start: 900, end: 1020 }, // 15:00 – 17:00
  { start: 1020, end: 1140 }, // 17:00 – 19:00
  { start: 1140, end: 1260 }, // 19:00 – 21:00
  { start: 1260, end: 1380 }, // 21:00 – 23:00
  { start: 1380, end: 1440 }, // 23:00 – 00:00
];

const timeOffset = ref(0);

const formatTime = (totalMinutes: number): string => {
  const normalized = ((totalMinutes % 1440) + 1440) % 1440;
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const chineseDoubleHours = computed(() => {
  return baseHours.map((interval: TimeInterval) => {
    const shiftedStart = formatTime(interval.start + timeOffset.value);
    const shiftedEnd = formatTime(interval.end + timeOffset.value);
    return `${shiftedStart} – ${shiftedEnd}`;
  });
});
</script>

<template>
  <DayCardComponent>
    <template #card-title>
      <p class="text-white text-2xl font-semibold">
        {{ t("calculators.chinese-calendar-day.hours-table.title") }}
      </p>
    </template>
    <template #card-content>
      <table>
        <tbody class="text-left">
          <tr v-for="(tableRow, idx) in hoursTable" :key="idx">
            <td
              class="border border-divider p-1 text-center text-sm md:text-base"
            >
              <p class="mb-1 font-bold whitespace-nowrap">
                {{ chineseDoubleHours[idx] }}
              </p>
              <p>{{ tableRow.hour_element }}</p>
            </td>
            <td class="border border-divider p-1">
              <div class="flex flex-col gap-2">
                <!-- PLUSES -->
                <template
                  v-for="(positiveActions, idx) in [
                    tableRow.actions.filter((a: ChineseCalendarAction) => a.is_positive),
                  ]"
                  :key="'positive-' + idx"
                >
                  <p v-if="positiveActions.length > 0" class="card-content-row">
                    {{
                      t("calculators.chinese-calendar-day.day-summary.pluses")
                    }}
                    <span>
                      {{
                        positiveActions
                          .map((a: ChineseCalendarAction) =>
                            t(`calculators.chinese-calendar.doing.${a.doing}`)
                          )
                          .join(", ")
                      }}
                    </span>
                  </p>
                </template>
                <!-- MINUSES -->
                <template
                  v-for="(negativeActions, idx) in [
                    tableRow.actions.filter((a: ChineseCalendarAction) => !a.is_positive),
                  ]"
                  :key="'negative-' + idx"
                >
                  <p v-if="negativeActions.length > 0" class="card-content-row">
                    {{
                      t("calculators.chinese-calendar-day.day-summary.minuses")
                    }}
                    <span>
                      {{
                        negativeActions
                          .map((a: ChineseCalendarAction) =>
                            t(`calculators.chinese-calendar.doing.${a.doing}`)
                          )
                          .join(", ")
                      }}
                    </span>
                  </p>
                </template>
                <!-- COLLISIONS -->
                <div v-if="tableRow.collisions !== undefined">
                  <div
                    class="my-2"
                    v-for="(collision, id) in tableRow.collisions"
                    :key="'collision-' + id"
                  >
                    <p class="entity-title text-error-dark">
                      {{ collision.name }}
                    </p>
                    <p class="card-content-row">{{ collision.desc }}</p>
                  </div>
                </div>
                <!-- SYMBOL STARS -->
                <div
                  v-if="tableRow.symbol_stars !== undefined"
                  class="flex flex-col gap-1"
                >
                  <div
                    class="my-2"
                    v-for="(star, id) in tableRow.symbol_stars"
                    :key="'star-' + id"
                  >
                    <p
                      class="entity-title"
                      :class="{
                        'text-error-dark': !star.is_positive,
                        'text-success': star.is_positive,
                      }"
                    >
                      {{ star.star }}
                    </p>
                    <p class="card-content-row">{{ star.desc }}</p>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </DayCardComponent>
</template>

<style scoped>
.card-content-row {
  @apply text-left font-medium text-sm md:text-lg text-brown;
  span {
    @apply font-semibold;
  }
}
.entity-title {
  @apply font-semibold text-sm md:text-xl text-left;
}
</style>
